<template>
	<form class="submit-form" @submit.prevent="submit">
		<p class="submit-form__req-hint">* Indicates required field</p>
		<TwoColumn class="fieldset">
			<FieldInput
				required
				name="name"
				label="Your real name"
				v-model="fields.name"
				:errors="errors.name"
			/>
			<FieldInput
				name="nickname"
				label="Display name"
				placeholder="Anonymous"
				v-model="fields.nickname"
				:errors="errors.nickname"
			/>
		</TwoColumn>
		<div v-if="isStandardMode" class="fieldset">
			<FieldInput
				type="email"
				name="email"
				label="Email"
				v-model="fields.email"
				:errors="errors.email"
			/>
		</div>
		<TwoColumn v-else-if="isCompetitionMode" class="fieldset">
			<FieldInput
				required
				name="studentNumber"
				label="Student number"
				info="From your QUT ID card"
				v-model="fields.studentNumber"
				:errors="errors.studentNumber"
			/>
			<FieldInput
				required
				type="email"
				name="email"
				label="Email"
				v-model="fields.email"
				:errors="errors.email"
			/>
		</TwoColumn>
		<!-- Title field for text submissions -->
		<div v-if="isText" class="fieldset">
			<FieldTextarea
				v-if="isText"
				required
				name="title"
				label="Write your idea"
				v-model="fields.title"
				:errors="errors.title"
			/>
		</div>
		<!-- Image upload -->
		<div class="fieldset" v-if="isImage">
			<FieldUpload
				required
				name="image"
				label="Upload your photo"
				:info="`PNG, JPG or GIF files up to ${sizeLimit} accepted`"
				v-model="fields.image"
				:errors="errors.image"
			/>
		</div>
		<!-- Drawing canvas -->
		<div class="fieldset" v-else-if="isDrawing">
			<Paint
				required
				name="drawing"
				label="Draw your idea"
				v-model="fields.drawing"
				:errors="errors.drawing"
			/>
		</div>
		<!-- Title field for non-text submissions -->
		<div v-if="!isText" class="fieldset">
			<FieldInput
				required
				name="title"
				label="Submission name"
				v-model="fields.title"
				:errors="errors.title"
			/>
		</div>
		<!-- T&Cs -->
		<div class="fieldset">
			<FieldCheckbox name="terms" v-model="fields.terms" required
				>I agree with the
				<a href="#" @click.prevent="showTerms"
					><em>ok</em>HiQ Terms &amp; Conditions</a
				><span v-if="isCompetitionMode">
					and the
					<a
						:href="campaign.termsLink"
						target="_blank"
						rel="noopener noreferrer"
						>Competition Terms &amp; Conditions</a
					></span
				></FieldCheckbox
			>
		</div>
		<!-- reCAPTCHA notice -->
		<div class="fieldset">
			<CaptchaNotice />
		</div>
		<!-- Submit -->
		<div class="fieldset">
			<SubmitButton v-bind="submitButton" label="Submit" />
		</div>
	</form>
</template>

<script>
import { enums, lib } from 'protoswipe-shared'
import Paint from '@/components/main/Paint'
import CaptchaNotice from '@/components/main/CaptchaNotice'
import FieldInput from '@/components/shared/FieldInput'
import FieldTextarea from '@/components/shared/FieldTextarea'
import FieldUpload from '@/components/shared/FieldUpload'
import FieldCheckbox from '@/components/shared/FieldCheckbox'
import SubmitButton from '@/components/shared/SubmitButton'
import TwoColumn from '@/components/shared/TwoColumn'
import form from '@/mixins/form'

const { CampaignType, CampaignMode } = enums
const { toBytes, SIZE_LIMIT } = lib.file

export default {
	name: 'submit-form',
	props: {
		campaign: Object
	},
	data() {
		return {
			sizeLimit: toBytes(SIZE_LIMIT)
		}
	},
	computed: {
		isText() {
			return this.campaign.type === CampaignType.text
		},
		isImage() {
			return this.campaign.type === CampaignType.image
		},
		isDrawing() {
			return this.campaign.type === CampaignType.drawing
		},
		isStandardMode() {
			return this.campaign.mode === CampaignMode.standard
		},
		isCompetitionMode() {
			return this.campaign.mode === CampaignMode.competition
		},
		titleLabel() {
			return this.isText ? 'Write your idea' : 'Submission name'
		}
	},
	methods: {
		showTerms() {
			this.$store.commit('modals/show', 'terms')
			return false
		}
	},
	created() {
		// Populate form with campaign details
		this.form.populate({
			campaignId: this.campaign.id,
			type: this.campaign.type,
			mode: this.campaign.mode
		})
	},
	mixins: [form.submission],
	components: {
		Paint,
		CaptchaNotice,
		FieldInput,
		FieldTextarea,
		FieldUpload,
		FieldCheckbox,
		SubmitButton,
		TwoColumn
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/layout';
@import '~scss/components';
@import '~scss/mq';

.submit-form {
	@include truncate-margin;

	&__req-hint {
		color: $black;
		font-size: 1.125rem;
		font-style: italic;
		margin-bottom: 2rem;
	}

	.fieldset {
		margin-bottom: 2rem;
	}
}
</style>
